import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { StoreState } from '../../../reducers';
import { MultiAppAuthenticationInfosState, AspConnexionStatus } from '../reducer';
import { MultiAppAuthenticationViewMode, AuthMethod } from '../constants';
import { MultiAppAuthenticationError, MultiAppAuthenticationOptionsWrapper, InputWrapper, ForgotPasswordLink, BackToLoginViewBlock } from '../styles';
import { AuthenticationForm, AuthenticateFormInputLogin, AuthenticateFormInputPhone, AuthenticateFormInputPassword,
	AuthenticateButton, AuthenticateFormInputCompany } from '../../Authentication/styles';
import LocalStorageManager from '../../../utils/localStorage/manager';
import UrlUtils from '../../../utils/url';
import { I18n, Translate } from 'react-redux-i18n';
import UserAgentUtils from '../../../utils/userAgent';
import { createNotification } from '../../NotificationBar/actions';
import { getAuthMethods, MultiAppAuthenticationInfosActionKeys, signIn } from '../actions';
import ApiUtils from '../../../utils/api';

export interface MultiAppAuthenticationFormProps {
	viewMode: MultiAppAuthenticationViewMode;
	switchViewMode: (viewMode: MultiAppAuthenticationViewMode) => void;
	setLoader: (hasLoader: boolean) => void;
	multiAppAuthenticationInfos: MultiAppAuthenticationInfosState;
	dispatch: Dispatch<StoreState>;
}

interface MultiAppAuthenticationState {
	login: string;
	password: string;
	phone: string;
	company: string;
	shouldRememberCredentials: boolean;
	errorMessage?: string;
}

enum FormFieldType {
	Email = 'email',
	Tel = 'tel',
	Password = 'password',
	Text = 'text'
}

export class MultiAppAuthenticationForm extends React.Component<MultiAppAuthenticationFormProps, MultiAppAuthenticationState> {

	constructor(props: MultiAppAuthenticationFormProps) {
		super(props);

		const { credentials } = props.multiAppAuthenticationInfos;

		this.state = {
			login: credentials && credentials.login ? credentials.login : '',
			password: credentials && credentials.password ? credentials.password : '',
			phone: '',
			company: '',
			shouldRememberCredentials: true
		};

		this.handleChangeFormField = this.handleChangeFormField.bind(this);
		this.handleSendForm = this.handleSendForm.bind(this);
		this.toggleRememberMe = this.toggleRememberMe.bind(this);
		this.onClickForgotPassword = this.onClickForgotPassword.bind(this);
	}

	handleChangeFormField(e: React.FormEvent<HTMLInputElement>) {
		const { shouldDisplayErrorMessage } = this;

		if (shouldDisplayErrorMessage) {
			this.resetErrorMessage();
		}

		switch (e.currentTarget.type) {
			case FormFieldType.Email:
				this.setState({ login: e.currentTarget.value });
				break;
			case FormFieldType.Password:
				this.setState({ password: e.currentTarget.value });
				break;
			case FormFieldType.Tel:
				this.setState({ phone: e.currentTarget.value });
				break;
			case FormFieldType.Text:
				this.setState({ company: e.currentTarget.value });
				break;
		}
	}

	handleSendForm(e: React.FormEvent<HTMLFormElement>): void {
		e.preventDefault();

		const { dispatch, switchViewMode, setLoader } = this.props;
		const { login, password, phone, company } = this.state;
		const { isLoginView, isCompanyView, } = this;

		if (!UserAgentUtils.isOnline()) {
			dispatch(createNotification('info.offline_login'));
			return;
		}

		if (!isCompanyView && (!login || !password || (!isLoginView && !phone))) {
			dispatch(createNotification('info.fill_all_input_fields'));
			return;
		}

		if (isCompanyView) {
			dispatch(getAuthMethods(company))
				.then(() => switchViewMode(MultiAppAuthenticationViewMode.Method))
				.catch(() => dispatch({ type: MultiAppAuthenticationInfosActionKeys.COMPANY_ERROR }));
		} else if (isLoginView) {
			dispatch(signIn({ login, password, method: AuthMethod.Webpublication }, this.state.shouldRememberCredentials));
		} else {
			setLoader(true);
			ApiUtils.createAspDemoAccount(login, password, phone)
				.then(() => {
					setLoader(false);
					this.setState({ errorMessage: undefined });
					dispatch(signIn({ login, password, method: AuthMethod.Webpublication }, this.state.shouldRememberCredentials));
				})
				.catch(reason => {
					setLoader(false);
					this.setState({ errorMessage: reason.message });
				});
		}
	}

	resetErrorMessage(): void {
		const { dispatch } = this.props;
		const { isLoginView, isCompanyView, } = this;

		if (isLoginView || isCompanyView) {
			dispatch({ type: MultiAppAuthenticationInfosActionKeys.RESET_STATUS });
		} else {
			this.setState({ errorMessage: undefined });
		}
	}

	toggleRememberMe(): void {
		const { shouldRememberCredentials } = this.state;
		if (shouldRememberCredentials) {
			LocalStorageManager.removeMultiAppCredentials();
		}
		this.setState({ shouldRememberCredentials: !shouldRememberCredentials });
	}

	onClickForgotPassword(): void {
		const url = UrlUtils.getResetAspPasswordUrl(ApiUtils.multiAppAuthenticationServer);
		window.open(url, '_blank');
	}

	get isLoginView(): boolean {
		return this.props.viewMode === MultiAppAuthenticationViewMode.Login;
	}

	get isCompanyView(): boolean {
		return this.props.viewMode === MultiAppAuthenticationViewMode.Company;
	}

	get isCreationView(): boolean {
		return this.props.viewMode === MultiAppAuthenticationViewMode.Creation;
	}

	get shouldDisplayErrorMessage(): boolean {
		const { isLoginView, isCompanyView, } = this;

		if (isLoginView || isCompanyView) {
			const { aspConnexionStatus } = this.props.multiAppAuthenticationInfos;
			return [AspConnexionStatus.AUTH_ERROR, AspConnexionStatus.NO_APP_ERROR, AspConnexionStatus.NO_COMPANY_ERROR].includes(aspConnexionStatus);
		} else {
			return !!this.state.errorMessage;
		}
	}

	get errorMessageKey(): string {
		const { isLoginView, isCompanyView, } = this;

		if (isLoginView) {
			const { aspConnexionStatus } = this.props.multiAppAuthenticationInfos;
			return aspConnexionStatus === AspConnexionStatus.AUTH_ERROR ? 'error.invalid_auth' : 'error.no_application_available';
		} else if (isCompanyView) {
			return 'error.invalid_company_name';
		} else {
			return this.state.errorMessage === 'DUPLICATE_EMAIL--email' ? 'error.duplicate_email' : 'error.unexpected_error';
		}
	}

	get submitButtonText(): string {
		const { isLoginView, isCompanyView, isCreationView } = this;

		if (isCompanyView) {
			return 'messages.next';
		} else if (isLoginView) {
			return 'messages.connect';
		} else if (isCreationView) {
			return 'messages.try_demo';
		} else {
			return '';
		}
	}

	render() {
		const { login, password, phone, company, shouldRememberCredentials } = this.state;
		const { shouldDisplayErrorMessage, isLoginView, isCompanyView, isCreationView } = this;
		const { switchViewMode } = this.props;

		return (
			<AuthenticationForm onSubmit={this.handleSendForm}>
				{!isCompanyView ?
					[<AuthenticateFormInputLogin
						value={login}
						onChange={this.handleChangeFormField}
						required={true}
						type="email"
						key="login"
						innerRef={(innerRef) => {
							if (innerRef) {
								innerRef.setAttribute('autocorrect', 'none');
								innerRef.setAttribute('autocapitalize', 'off');
								innerRef.setAttribute('spellcheck', 'false');
							}
						}}
						placeholder={I18n.t('messages.placeholder_email')}
					/>,
					<AuthenticateFormInputPassword
						value={password}
						onChange={this.handleChangeFormField}
						required={true}
						type="password"
						key="password"
						minLength={6}
						placeholder={I18n.t('messages.placeholder_password')}
					/>]
					: <AuthenticateFormInputCompany
						value={company}
						onChange={this.handleChangeFormField}
						required={true}
						type="text"
						key="company"
						placeholder={I18n.t('messages.placeholder_company')}
					/>
				}
				{isCreationView &&
					<AuthenticateFormInputPhone
						value={phone}
						onChange={this.handleChangeFormField}
						required={true}
						type="tel"
						key="phone"
						placeholder={I18n.t('messages.placeholder_phone')}
					/>
				}
				{shouldDisplayErrorMessage &&
					<MultiAppAuthenticationError>
						<Translate value={this.errorMessageKey} />
					</MultiAppAuthenticationError>
				}
				<AuthenticateButton type="submit">
					<Translate value={this.submitButtonText} />
				</AuthenticateButton>
				{isLoginView &&
					<MultiAppAuthenticationOptionsWrapper>
						<InputWrapper onClick={this.toggleRememberMe}>
							<input type="checkbox" checked={shouldRememberCredentials} />
							<div />
							<label><Translate value="messages.remember_me" /></label>
						</InputWrapper>
						<ForgotPasswordLink onClick={this.onClickForgotPassword} href="javascript:void();">
							<Translate value="messages.forgot_password" />
						</ForgotPasswordLink>
					</MultiAppAuthenticationOptionsWrapper>
				}
				{(isCreationView || isLoginView) &&
					<BackToLoginViewBlock onClick={() => switchViewMode(isCreationView ? MultiAppAuthenticationViewMode.Company : MultiAppAuthenticationViewMode.Method)}>
						<Translate value={isCreationView ? 'messages.back_to_company_name' : 'messages.back_to_method_view'} />
					</BackToLoginViewBlock>
				}
			</AuthenticationForm>

		);
	}
}

const mapStateToProps = (state: StoreState) => {
	const { multiAppAuthenticationInfos } = state;
	return { multiAppAuthenticationInfos };
};

export default connect(mapStateToProps)(MultiAppAuthenticationForm);


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/MultiAppAuthentication/Form/index.tsx